import * as Sentry from '@sentry/react';

/** Keeps "the backend sent nothing" (or something unparseable) as `null` instead of flattening it to a number. */
export function toNullableNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value);
  return null;
}

/** Coerces Decimal/BigInt strings and numbers to a finite number, `fallback` otherwise. */
export function toNumber(value: unknown, fallback = 0): number {
  return toNullableNumber(value) ?? fallback;
}

/** Growth percentage: the backend sends a ratio (0.5 = +50%) but the UI expects a percent (50); keeps `null` when the backend has no comparison. */
export function toPercentage(value: unknown): number | null {
  const ratio = toNullableNumber(value);
  return ratio === null ? null : ratio * 100;
}

/**
 * Cheap unit-mismatch tripwire: a ratio field above `maxPlausible` almost always means the
 * backend sent a percent where a fraction was expected (or vice versa). Warns and reports at
 * `warning` severity without blocking rendering — never throws.
 */
export function warnIfImplausible(label: string, value: number | null, maxPlausible: number): void {
  if (value === null || value <= maxPlausible) return;
  if (import.meta.env.DEV) console.warn(`Implausible ${label} value: ${value} (expected <= ${maxPlausible})`);
  Sentry.captureMessage(`Implausible scorecard value for ${label}: ${value}`, 'warning');
}

/** Unique report dates in ascending order — defensive against unsorted or duplicate-date rows from the backend. */
export function sortedUniqueDates(periods: ReadonlyArray<{ reportDate: string }>): string[] {
  return [...new Set(periods.map((period) => period.reportDate))].sort((a, b) => a.localeCompare(b));
}

/** Maps each date to a metric value; last matching period wins if a date repeats (mirrors `mapCampaignTypeToStackedModel`). */
export function seriesByDate<TPeriod extends { reportDate: string }>(
  periods: ReadonlyArray<TPeriod>,
  dates: readonly string[],
  valueOf: (period: TPeriod) => number
): number[] {
  const valueByDate = new Map<string, number>();
  for (const period of periods) valueByDate.set(period.reportDate, valueOf(period));
  return dates.map((date) => valueByDate.get(date) ?? 0);
}
